import { useState, useEffect, useRef, useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import KanbanColumn from "@/components/kanban/KanbanColumn";
import AddTaskModal from "@/components/kanban/AddTaskModal";
import AddColumnModal from "@/components/kanban/AddColumnModal";
import TransferMFOModal from "@/components/kanban/TransferMFOModal";
import DeleteMFOConfirmModal from "@/components/kanban/DeleteMFOConfirmModal";
import useKanbanBoard from "@/hooks/useKanbanBoard";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { Plus, ArrowLeftRight, GripHorizontal, Filter } from "lucide-react";
import {
	DndContext,
	DragEndEvent,
	DragStartEvent,
	useSensor,
	useSensors,
	PointerSensor,
	DragOverlay,
} from "@dnd-kit/core";
import {
	SortableContext,
	arrayMove,
	horizontalListSortingStrategy,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { KanbanCard } from "@/types/kanban";
import { createNotification } from "@/utils/notificationUtils";
import TaskCardWrapper from "@/components/kanban/TaskCardWrapper";

const defaultColumns = [
	{
		title: "Not Started",
		icon_name: "Clock",
		icon_color: "#64748b",
		order_index: 0,
	},
	{
		title: "In Progress",
		icon_name: "CircleCheck",
		icon_color: "#3b82f6",
		order_index: 1,
	},
	{
		title: "Delayed",
		icon_name: "AlertTriangle",
		icon_color: "#f59e0b",
		order_index: 2,
	},
	{
		title: "Completed",
		icon_name: "CheckCheck",
		icon_color: "#22c55e",
		order_index: 3,
	},
];

const KanbanBoard = () => {
	const {
		columns,
		setColumns,
		cards,
		isLoading,
		isModalOpen,
		setIsModalOpen,
		newTaskColumn,
		newTask,
		setNewTask,
		handleDragStart: kanbanHandleDragStart,
		handleDragOver,
		handleDrop,
		openAddTaskModal,
		handleAddTask,
		handleDeleteTask,
		handleTransferTask,
		fetchAllData,
		createColumn,
		reorderColumn,
		moveCard,
	} = useKanbanBoard();

	const [taskToDelete, setTaskToDelete] = useState<string | null>(null);
	const [taskToTransfer, setTaskToTransfer] = useState<string | null>(null);
	const [targetColumn, setTargetColumn] = useState<string | null>(null);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
	const [isAddColumnModalOpen, setIsAddColumnModalOpen] = useState(false);
	const [isInitialSetupDone, setIsInitialSetupDone] = useState(false);
	const [activeColumnId, setActiveColumnId] = useState<string | null>(null);
	const [activeCardId, setActiveCardId] = useState<string | null>(null);
	const [isColumnReorderMode, setIsColumnReorderMode] = useState(false);
	const [showCreateModal, setShowCreateModal] = useState(false);
	const [showFilterModal, setShowFilterModal] = useState(false);
	const modalRef = useRef<HTMLDivElement>(null);
	const { toast } = useToast();
	const [activeDragType, setActiveDragType] = useState<'column' | 'card' | null>(null);
	const [activeDragItem, setActiveDragItem] = useState<any>(null);
	const allCardIds = cards.map(card => card.id);

	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 5,
			},
		})
	);

	const checkColumnsExist = useCallback(async () => {
		try {
			const { data, error } = await supabase
				.from("kanban_columns")
				.select("id")
				.limit(1);

			if (error) throw error;

			if (!data || data.length === 0) {
				if (!isInitialSetupDone) {
					await setupInitialColumns();
					setIsInitialSetupDone(true);
				}
			} else {
				fetchAllData();
			}
		} catch (error) {
			console.error("Error checking columns:", error);
		}
	}, [fetchAllData, isInitialSetupDone]);

	useEffect(() => {
		checkColumnsExist();
	}, [checkColumnsExist]);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				isModalOpen ||
				isDeleteModalOpen ||
				isTransferModalOpen ||
				isAddColumnModalOpen
			) {
				document.body.classList.add("modal-open");
			} else {
				document.body.classList.remove("modal-open");
			}
		};

		document.addEventListener("mousedown", handleClickOutside);

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
			document.body.classList.remove("modal-open");
		};
	}, [
		isModalOpen,
		isDeleteModalOpen,
		isTransferModalOpen,
		isAddColumnModalOpen,
	]);

	const setupInitialColumns = async () => {
		try {
			for (const column of defaultColumns) {
				await supabase.from("kanban_columns").insert(column);
			}

			fetchAllData();
		} catch (error) {
			console.error("Error setting up initial columns:", error);
		}
	};

	const openDeleteModal = (taskId: string) => {
		setTaskToDelete(taskId);
		setIsDeleteModalOpen(true);
	};

	const closeDeleteModal = () => {
		setTaskToDelete(null);
		setIsDeleteModalOpen(false);
	};

	const confirmDelete = () => {
		if (taskToDelete) {
			handleDeleteTask(taskToDelete);
			closeDeleteModal();
		}
	};

	const openTransferModal = (taskId: string) => {
		setTaskToTransfer(taskId);
		setIsTransferModalOpen(true);
	};

	const closeTransferModal = () => {
		setTaskToTransfer(null);
		setTargetColumn(null);
		setIsTransferModalOpen(false);
	};

	const confirmTransfer = () => {
		if (taskToTransfer && targetColumn) {
			handleTransferTask(taskToTransfer, targetColumn);
			closeTransferModal();
		}
	};

	const handleAddColumn = (columnData: {
		title: string;
		icon_name: string;
		icon_color: string;
	}) => {
		createColumn(columnData.title, columnData.icon_name, columnData.icon_color);
		toast({
			title: "Column Added",
			description: `Column "${columnData.title}" has been created.`,
		});
	};

	const handleOpenAddColumnModal = () => {
		setIsAddColumnModalOpen(true);
	};

	const handleDragStart = (event: DragStartEvent) => {
		if (isColumnReorderMode && event.active.data.current?.type === 'column') {
			setActiveDragType('column');
			setActiveDragItem(event.active.data.current);
			setActiveColumnId(event.active.id as string);
		} else if (!isColumnReorderMode && event.active.data.current?.type === 'card') {
			setActiveDragType('card');
			setActiveDragItem(event.active.data.current);
			setActiveCardId(event.active.id as string);
		}
	};

	const handleColumnDragEnd = (event: DragEndEvent) => {
		if (!isColumnReorderMode) return;
		const { active, over } = event;

		if (!over || active.id === over.id) {
			setActiveColumnId(null);
			return;
		}

		const oldIndex = columns.findIndex((col) => col.id === active.id);
		const newIndex = columns.findIndex((col) => col.id === over.id);

		if (oldIndex !== -1 && newIndex !== -1) {
			const newColumns = arrayMove(columns, oldIndex, newIndex);
			setColumns(newColumns);

			reorderColumn(oldIndex, newIndex);
		}

		setActiveColumnId(null);
	};

	const handleCardDragEnd = (event: DragEndEvent) => {
		if (isColumnReorderMode) return;
		const { active, over } = event;
		if (!over) return;

		const cardId = active.id as string;
		let targetColumnId: string | null = null;

		// If dropped on a card, find its column
		const overCard = cards.find(card => card.id === over.id);
		if (overCard) {
			targetColumnId = overCard.columnId;
		} else {
			// If dropped on a column, over.id is the column id
			targetColumnId = over.id as string;
		}

		const card = cards.find(c => c.id === cardId);
		if (card && targetColumnId && card.columnId !== targetColumnId) {
			moveCard(cardId, targetColumnId);

			// Create a notification for the card move
			const cardTitle = card.title;
			const columnTitle = columns.find(col => col.id === targetColumnId)?.title || "";

			createNotification(
				"Task Moved",
				`Task "${cardTitle}" has been moved to "${columnTitle}"`,
				"task_moved"
			);
		}

		setActiveCardId(null);
	};

	const checkDeadlines = useCallback(() => {
		const today = new Date();
		const sevenDaysLater = new Date(today);
		sevenDaysLater.setDate(today.getDate() + 7);

		cards.forEach((card) => {
			if (!card.dueDate) return;

			const dueDate = new Date(card.dueDate);

			// If the due date is within the next 7 days and not past
			if (dueDate > today && dueDate <= sevenDaysLater) {
				const daysUntilDue = Math.ceil(
					(dueDate.getTime() - today.getTime()) / (1000 * 3600 * 24)
				);

				// Create notification for approaching deadline
				createNotification(
					"Deadline Approaching",
					`Task "${card.title}" is due in ${daysUntilDue} day${daysUntilDue !== 1 ? "s" : ""}`,
					"deadline_approaching"
				);
			}
		});
	}, [cards]);

	// Check for approaching deadlines when cards change
	useEffect(() => {
		if (cards.length > 0) {
			checkDeadlines();
		}
	}, [cards, checkDeadlines]);

	if (isLoading) {
		return (
			<div className='space-y-6'>
				<div className='flex justify-between items-center'>
					<Skeleton className='h-9 w-64' />
					<Skeleton className='h-10 w-32' />
				</div>

				<div className='grid grid-cols-1 gap-4'>
					<Card>
						<CardHeader className='pb-2'>
							<CardTitle>Tasks</CardTitle>
						</CardHeader>
						<CardContent>
							<div className='flex overflow-x-auto gap-4 pb-4'>
								{Array(4)
									.fill(0)
									.map((_, i) => (
										<div
											key={i}
											className='min-w-[300px] max-w-[300px] flex flex-col rounded-lg bg-muted/40 border'>
											<div className='p-3 font-medium flex items-center justify-between border-b'>
												<Skeleton className='h-6 w-32' />
												<Skeleton className='h-7 w-7 rounded-full' />
											</div>
											<div className='p-2 space-y-2'>
												{Array(3)
													.fill(0)
													.map((_, j) => (
														<Skeleton
															key={j}
															className='h-32 w-full'
														/>
													))}
											</div>
										</div>
									))}
							</div>
						</CardContent>
					</Card>
				</div>
			</div>
		);
	}

	return (
		<div className='space-y-6'>
			<div className='flex justify-between items-center'>
				<h1 className='text-3xl font-bold'>MFO Management</h1>
				<div className='flex gap-2'>
					<Button
						variant='outline'
						onClick={handleOpenAddColumnModal}>
						<Plus className='h-4 w-4 mr-2' />
						Add Column
					</Button>
					<Button
						variant='outline'
						onClick={() => setShowFilterModal(true)}>
						<Filter className='h-4 w-4 mr-2' />
						Filter
					</Button>
				</div>
			</div>

			<div className='grid grid-cols-1 gap-4'>
				<Card>
					<CardHeader className='pb-2'>
						<div className='flex justify-between items-center'>
							<CardTitle className='flex items-center'>
								Tasks
								{isColumnReorderMode && (
									<GripHorizontal className='ml-2 h-4 w-4 text-muted-foreground' />
								)}
							</CardTitle>
							<div className='flex items-center space-x-2'>
								<Switch
									id='column-reorder'
									checked={isColumnReorderMode}
									onCheckedChange={setIsColumnReorderMode}
								/>
								<Label
									htmlFor='column-reorder'
									className='text-sm'>
									Column Reorder Mode
								</Label>
							</div>
						</div>
					</CardHeader>
					<CardContent>
						<DndContext
							sensors={sensors}
							onDragStart={handleDragStart}
							onDragEnd={handleCardDragEnd}
						>
							<SortableContext
								items={columns.map((col) => col.id)}
								strategy={horizontalListSortingStrategy}
							>
								<div className='flex overflow-x-auto gap-4 pb-4'>
									{columns.map((column) => {
										return (
											<KanbanColumn
												key={column.id}
												column={{
													...column,
													iconName: column.icon_name || column.iconName || "",
													iconColor: column.icon_color || column.iconColor || "",
												}}
												isActive={activeColumnId === column.id}
												isColumnReorderMode={isColumnReorderMode}
												onDragStart={handleDragStart}
												onDragOver={!isColumnReorderMode ? handleDragOver : undefined}
												onDrop={!isColumnReorderMode ? handleDrop : undefined}
												onAddTask={() => openAddTaskModal(column.id)}
												onDeleteTask={openDeleteModal}
												onTransferTask={openTransferModal}
												employees={[]}
												activeDragType={activeDragType}
												activeDragItem={activeDragItem}
											/>
										);
									})}
								</div>
							</SortableContext>
							<SortableContext items={allCardIds} strategy={verticalListSortingStrategy}>
								<DragOverlay>
									{activeDragType === 'card' && activeDragItem ? (
										<TaskCardWrapper
											card={activeDragItem}
											columnId={activeDragItem.columnId}
											isDraggingOverlay
										/>
									) : activeDragType === 'column' && activeDragItem ? (
										<KanbanColumn
											column={activeDragItem}
											isActive
											isColumnReorderMode={isColumnReorderMode}
											isDraggingOverlay
										/>
									) : null}
								</DragOverlay>
							</SortableContext>
						</DndContext>
					</CardContent>
				</Card>
			</div>

			<div ref={modalRef}>
				<AddTaskModal
					isOpen={isModalOpen}
					onOpenChange={setIsModalOpen}
					newTaskColumn={newTaskColumn}
					onAddTask={() => handleAddTask(newTask)}
					newTask={newTask}
					setNewTask={setNewTask}
				/>

				<AddColumnModal
					isOpen={isAddColumnModalOpen}
					onOpenChange={setIsAddColumnModalOpen}
					onAddColumn={handleAddColumn}
				/>

				<DeleteMFOConfirmModal
					isOpen={isDeleteModalOpen}
					onOpenChange={setIsDeleteModalOpen}
					mfoTitle={
						taskToDelete
							? columns
									.flatMap((col) => col.cards)
									.find((card) => card.id === taskToDelete)?.title || ""
							: ""
					}
					onConfirm={confirmDelete}
				/>

				<TransferMFOModal
					isOpen={isTransferModalOpen}
					onOpenChange={setIsTransferModalOpen}
					mfo={{
						id: taskToTransfer || "",
						code: "",
						title: taskToTransfer
							? columns
									.flatMap((col) => col.cards)
									.find((card) => card.id === taskToTransfer)?.title || ""
							: "",
						successIndicators: [],
						budget: 0,
						timeline: { q1: false, q2: false, q3: false, q4: false },
						collaborators: [],
						departmentId: "",
						departmentName: "",
						status: "",
						priority: "",
						progress: 0,
						dueDate: "",
					}}
					currentColumnId={
						taskToTransfer
							? columns.find((col) =>
									col.cards.some((card) => card.id === taskToTransfer)
								)?.id || ""
							: ""
					}
					onTransfer={(newColumnId: string) => {
						setTargetColumn(newColumnId);
						confirmTransfer();
					}}
				/>
			</div>

			<style>
				{`
        .modal-open .kanban-column,
        .modal-open .kanban-card {
          pointer-events: none;
        }
        
        .modal-open [role="dialog"] {
          pointer-events: auto;
        }
        
        .kanban-column-content {
          max-height: calc(100vh - 260px);
          overflow-y: auto;
          overflow-x: hidden;
          scrollbar-width: thin;
        }
        
        .kanban-column-content::-webkit-scrollbar {
          width: 6px;
        }
        
        .kanban-column-content::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.05);
          border-radius: 3px;
        }
        
        .kanban-column-content::-webkit-scrollbar-thumb {
          background: rgba(0, 0, 0, 0.2);
          border-radius: 3px;
        }
        
        .kanban-column-content::-webkit-scrollbar-thumb:hover {
          background: rgba(0, 0, 0, 0.3);
        }
        `}
			</style>
		</div>
	);
};

export default KanbanBoard;
